import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { OwnerAvatar } from '@/shared/components/OwnerAvatar';
import { TodoStatusBadge } from './TodoStatusBadge';
import { TodoPriorityBadge } from './TodoPriorityBadge';
import {
  useCompleteEosTodo, useReopenEosTodo,
} from '../hooks/useEosTodoMutations';
import type { EosTodo } from '../types';

interface Props {
  todos: EosTodo[];
  isLoading: boolean;
  onSelect: (todo: EosTodo) => void;
  ownerLabel: (ownerId: string) => string;
  teamLabel: (teamId: string | null) => string;
}

export function TodoListView({ todos, isLoading, onSelect, ownerLabel, teamLabel }: Props) {
  const complete = useCompleteEosTodo();
  const reopen = useReopenEosTodo();

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10"></TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Team</TableHead>
            <TableHead>Due</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={i}>
                {Array.from({ length: 7 }).map((_, j) => (
                  <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                ))}
              </TableRow>
            ))
          ) : todos.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-10">
                No To-Dos match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            todos.map((t) => (
              <TableRow
                key={t.id}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => onSelect(t)}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={t.status === 'done'}
                    onCheckedChange={() => {
                      if (t.status === 'done') reopen.mutate(t.id);
                      else complete.mutate(t.id);
                    }}
                  />
                </TableCell>
                <TableCell className="font-medium">{t.title}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <OwnerAvatar ownerId={t.ownerId} name={ownerLabel(t.ownerId)} />
                    <span>{ownerLabel(t.ownerId)}</span>
                  </div>
                </TableCell>
                <TableCell>{teamLabel(t.teamId)}</TableCell>
                <TableCell>{t.dueDate}</TableCell>
                <TableCell><TodoPriorityBadge priority={t.priority} /></TableCell>
                <TableCell><TodoStatusBadge status={t.status} /></TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
